/**
 * Service Type Resolver
 * Maps maintenance job IDs to normalized service type identifiers
 *
 * This is the SINGLE SOURCE OF TRUTH for service type identification.
 * Used across badge calculation, equipment updates, and status checks.
 */

/**
 * Determine service type from maintenance job
 * @param {Object} job - Maintenance job object
 * @returns {string} Normalized service type identifier
 */
export function serviceTypeFromJob(job) {
  // Build search string from all job identifiers
  const searchStr = [
    job.id || '',
    job.templateId || '',
    job.equipmentType || '',
    job.checkType || '',
    job.item || '',
    job.name || ''
  ].join('|').toLowerCase();

  // FIRE EXTINGUISHERS - Three service cycles
  if (searchStr.includes('extinguisher')) {
    if (searchStr.includes('extended') || searchStr.includes('5y') || searchStr.includes('5-year')) {
      return 'extinguisher_extended';
    }
    if (searchStr.includes('overhaul') || searchStr.includes('10y') || searchStr.includes('10-year')) {
      return 'extinguisher_overhaul';
    }
    // Default to basic/annual service
    return 'extinguisher_basic';
  }

  // FIXED FIRE SYSTEMS
  if (searchStr.includes('fixed') && searchStr.includes('system')) {
    if (searchStr.includes('engine') || searchStr.includes('machinery')) {
      return 'fixed_system_engine';
    }
    if (searchStr.includes('galley') || searchStr.includes('cooking')) {
      return 'fixed_system_galley';
    }
    if (searchStr.includes('accommodation')) {
      return 'fixed_system_accommodation';
    }
    return 'fixed_system_generic';
  }

  // DETECTORS - Differentiate by type AND service interval
  if (searchStr.includes('detector') || searchStr.includes('detection')) {
    // CO Detector - differentiate monthly test vs annual calibration
    if (searchStr.includes('co ') || searchStr.includes('co_') || searchStr.includes('carbon monoxide')) {
      if (searchStr.includes('test') || searchStr.includes('monthly')) {
        return 'detector_co_monthly';
      }
      if (searchStr.includes('annual') || searchStr.includes('calibration')) {
        return 'detector_co_annual';
      }
      return 'detector_co';
    }
    // Gas/Hydrocarbon Detector - differentiate monthly vs annual
    if (searchStr.includes('gas') || searchStr.includes('hydrocarbon')) {
      if (searchStr.includes('test') || searchStr.includes('monthly')) {
        return 'detector_gas_monthly';
      }
      if (searchStr.includes('annual') || searchStr.includes('calibration')) {
        return 'detector_gas_annual';
      }
      return 'detector_gas';
    }
    // Fire Detector (smoke, heat) - differentiate monthly vs annual
    if (searchStr.includes('fire') || searchStr.includes('smoke') || searchStr.includes('heat')) {
      if (searchStr.includes('test') || searchStr.includes('monthly')) {
        return 'detector_fire_monthly';
      }
      if (searchStr.includes('annual') || searchStr.includes('service')) {
        return 'detector_fire_annual';
      }
      return 'detector_fire';
    }
    // Lithium battery detector
    if (searchStr.includes('lithium') || searchStr.includes('li-ion')) {
      return 'detector_lithium';
    }
    // Generic detector fallback
    return 'detector_generic';
  }

  // FIRE FIGHTING EQUIPMENT
  if (searchStr.includes('fire pump') || searchStr.includes('pump')) return 'pump';
  if (searchStr.includes('fire hose') || searchStr.includes('hose')) return 'hose';
  if (searchStr.includes('fire blanket') || searchStr.includes('blanket')) return 'blanket';
  if (searchStr.includes('fire bucket') || searchStr.includes('bucket')) return 'bucket';

  // LIFE SAVING APPLIANCES
  if (searchStr.includes('liferaft') || searchStr.includes('life raft')) return 'liferaft';
  if (searchStr.includes('lifejacket') || searchStr.includes('life jacket')) return 'lifejacket';
  if (searchStr.includes('lifebuoy')) return 'lifebuoy';
  if (searchStr.includes('epirb')) return 'epirb';
  if (searchStr.includes('sart')) return 'sart';

  // NAVIGATION & RADIO
  if (searchStr.includes('compass')) return 'compass';
  if (searchStr.includes('radar')) return 'radar';
  if (searchStr.includes('gps')) return 'gps';
  if (searchStr.includes('vhf') || searchStr.includes('radio')) return 'radio';

  // MEDICAL - Distinct service types per job (from MEDICAL_v1/MAINTENANCE.csv)
  if (searchStr.includes('medical')) {
    // Expiry checks - separate by category (Cat A, B, C)
    if (searchStr.includes('expiry') && searchStr.includes('cat a')) {
      return 'medical_expiry_cat_a';
    }
    if (searchStr.includes('expiry') && searchStr.includes('cat b')) {
      return 'medical_expiry_cat_b';
    }
    if (searchStr.includes('expiry') && (searchStr.includes('cat c') || searchStr.includes('sealed'))) {
      return 'medical_expiry_cat_c';
    }
    // Annual reviews - separate by type
    if (searchStr.includes('checklist') && searchStr.includes('review')) {
      return 'medical_checklist_review';
    }
    if (searchStr.includes('sealed') && searchStr.includes('review')) {
      return 'medical_sealed_review';
    }
    // Generic expiry fallback (if category not specified)
    if (searchStr.includes('expiry')) {
      return 'medical_expiry';
    }
    // Generic medical fallback
    return 'medical_kit';
  }

  // DOCUMENTATION
  if (searchStr.includes('plan') || searchStr.includes('document')) return 'documentation';

  // Generic fallback
  return 'generic';
}

/**
 * Get human-readable service type name
 * @param {string} serviceType - Service type identifier
 * @returns {string} Display name
 */
export function getServiceTypeName(serviceType) {
  const names = {
    // Extinguishers
    'extinguisher_basic': 'Basic Service (Annual)',
    'extinguisher_extended': 'Extended Service (5-Year)',
    'extinguisher_overhaul': 'Overhaul (10-Year)',

    // Fixed Systems
    'fixed_system_engine': 'Engine Space System',
    'fixed_system_galley': 'Galley System',
    'fixed_system_accommodation': 'Accommodation System',
    'fixed_system_generic': 'Fixed System',

    // Detectors
    'detector_fire': 'Fire Detection',
    'detector_fire_monthly': 'Fire Detection - Monthly Test',
    'detector_fire_annual': 'Fire Detection - Annual Service',
    'detector_co': 'CO Detection',
    'detector_co_monthly': 'CO Detection - Monthly Test',
    'detector_co_annual': 'CO Detection - Annual Calibration',
    'detector_gas': 'Gas Detection',
    'detector_gas_monthly': 'Gas Detection - Monthly Test',
    'detector_gas_annual': 'Gas Detection - Annual Calibration',
    'detector_lithium': 'Lithium Battery Detection',
    'detector_generic': 'Detection System',

    // Other equipment
    'pump': 'Fire Pump',
    'hose': 'Fire Hose',
    'blanket': 'Fire Blanket',
    'bucket': 'Fire Bucket',
    'liferaft': 'Liferaft',
    'lifejacket': 'Lifejacket',
    'lifebuoy': 'Lifebuoy',
    'epirb': 'EPIRB',
    'sart': 'SART',
    'compass': 'Compass',
    'radar': 'Radar',
    'gps': 'GPS',
    'radio': 'Radio',
    'medical_kit': 'Medical Kit',
    'medical_expiry_cat_a': 'Medical Stores Expiry Check (Cat A)',
    'medical_expiry_cat_b': 'Medical Stores Expiry Check (Cat B)',
    'medical_expiry_cat_c': 'Medical Stores Expiry Check (Cat C)',
    'medical_expiry': 'Medical Stores Expiry Check',
    'medical_checklist_review': 'Medical Stores Checklist Review',
    'medical_sealed_review': 'Medical Stores Sealed Kit Review',
    'documentation': 'Documentation',
    'generic': 'General Service'
  };

  return names[serviceType] || serviceType;
}

/**
 * Get service interval description
 * @param {string} serviceType - Service type identifier
 * @returns {string} Interval description
 */
export function getServiceInterval(serviceType) {
  const intervals = {
    'extinguisher_basic': '1 year',
    'extinguisher_extended': '5 years',
    'extinguisher_overhaul': '10 years',
    'fixed_system_engine': '1 year',
    'fixed_system_galley': '1 year',
    'fixed_system_accommodation': '1 year',
    'detector_fire': '1 month',
    'detector_co': '1 month',
    'detector_gas': '1 month',
    'detector_lithium': '1 month',
    'liferaft': 'Per manufacturer',
    'lifejacket': '1 year',
    'epirb': 'Per manufacturer'
  };

  return intervals[serviceType] || 'Per schedule';
}

/**
 * Get the last service date for a specific service type from equipment history
 * @param {Object} equipment - Equipment item with serviceHistory array
 * @param {string} jobId - Job ID to determine service type
 * @returns {string|null} - ISO date string of last service, or null if never serviced
 */
export function getLastServiceDateByType(equipment, jobId) {
  // If no service history, return null
  if (!equipment.serviceHistory || equipment.serviceHistory.length === 0) {
    return null;
  }

  // Determine service type from job ID
  // For CO Detector jobs:
  // - 'ffe.detector.co.test' -> monthly test
  // - 'ffe.detector.co.annual' -> annual calibration
  let serviceTypeFilter;
  
  if (jobId.includes('.test') || jobId.includes('.monthly')) {
    serviceTypeFilter = 'monthly_test';
  } else if (jobId.includes('.annual') || jobId.includes('.calibration')) {
    serviceTypeFilter = 'annual_service';
  } else {
    // For other job types, use a general approach
    serviceTypeFilter = jobId;
  }

  // Find the most recent service matching this type
  const relevantServices = equipment.serviceHistory
    .filter(h => {
      // Match by job ID or service type
      return h.jobId === jobId || h.serviceType === serviceTypeFilter;
    })
    .sort((a, b) => {
      // Sort by date, most recent first
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });

  // Return the most recent service date
  return relevantServices.length > 0 ? relevantServices[0].date : null;
}
